import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Upload, FileText, Download, Trash2, File as FileIcon, FileImage, FileSpreadsheet,
  Plus, Presentation, ExternalLink, ArrowLeft, FolderOpen, Cloud, Users, Lock, ShieldCheck, Check, Pin, PinOff, Search, Share2, FolderInput,
} from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

const COLLAB_TYPE = {
  doc: { label: "Google Doc", icon: FileText, color: "#17A88A", bg: "#17A88A1A" },
  sheet: { label: "Google Sheet", icon: FileSpreadsheet, color: "#4A7A4E", bg: "#4A7A4E1A" },
  slides: { label: "Google Slides", icon: Presentation, color: "#0B5E4E", bg: "#0B5E4E1A" },
  other: { label: "Document", icon: FileIcon, color: "#56626A", bg: "#56626A1A" },
};

function iconFor(ext) {
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return FileImage;
  if (["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (ext === "pdf") return FileText;
  return FileIcon;
}
function humanSize(n) { if (!n) return "—"; const u = ["B", "KB", "MB", "GB"]; let i = 0; while (n >= 1024 && i < 3) { n /= 1024; i++; } return `${n.toFixed(1)} ${u[i]}`; }

// Reusable checklist of team members with a "select all" toggle
function MemberPicker({ members, selected, onToggle, onSelectAll, testidPrefix }) {
  if (!members.length) {
    return <p className="text-sm py-3" style={{ color: "var(--sj-text-3)" }}>This team has no members yet. Add members to the team first, or nobody will be able to open it.</p>;
  }
  const allOn = members.every((m) => selected.includes(m.id));
  return (
    <div>
      <button type="button" data-testid={`${testidPrefix}-select-all`} onClick={() => onSelectAll(!allOn)}
        className="text-xs font-semibold mb-2 inline-flex items-center gap-1.5" style={{ color: "var(--sj-primary)" }}>
        <ShieldCheck className="h-3.5 w-3.5" /> {allOn ? "Clear all" : "Select all team members"}
      </button>
      <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
        {members.map((m) => {
          const on = selected.includes(m.id);
          return (
            <button type="button" key={m.id} data-testid={`${testidPrefix}-member-${m.id}`} onClick={() => onToggle(m.id)}
              className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors"
              style={on ? { background: "#0B5E4E1A", color: "var(--sj-primary)" } : { color: "var(--sj-text)", border: "1px solid var(--sj-border)" }}>
              <span className="flex items-center gap-2 min-w-0">
                <span className="truncate">{m.name}</span>
                {!m.has_login && <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#D9770622", color: "#B45309" }}>no login</span>}
              </span>
              <span className="h-5 w-5 rounded-md flex items-center justify-center shrink-0" style={on ? { background: "var(--sj-primary)" } : { border: "1px solid var(--sj-border)" }}>
                {on && <Check className="h-3.5 w-3.5 text-white" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Documents() {
  const [tab, setTab] = useState("files");
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");

  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [teamId, setTeamId] = useState("");
  const [folder, setFolder] = useState("");
  const [uploadMembers, setUploadMembers] = useState([]); // team members for upload picker
  const [uploadSelected, setUploadSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const [collab, setCollab] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", team_id: "", folder: "" });
  const [collabMembers, setCollabMembers] = useState([]);
  const [collabSelected, setCollabSelected] = useState([]);
  const [active, setActive] = useState(null);

  // access management dialog
  const [accessFor, setAccessFor] = useState(null); // {type, doc}
  const [accessMembers, setAccessMembers] = useState([]);
  const [accessSelected, setAccessSelected] = useState([]);
  const [accessFolder, setAccessFolder] = useState("");
  const [savingAccess, setSavingAccess] = useState(false);

  // bulk share dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTeam, setBulkTeam] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkCollab, setBulkCollab] = useState([]);
  const [bulkMembers, setBulkMembers] = useState([]);
  const [bulkSelFiles, setBulkSelFiles] = useState([]);
  const [bulkSelCollab, setBulkSelCollab] = useState([]);
  const [bulkSelMembers, setBulkSelMembers] = useState([]);
  const [sharing, setSharing] = useState(false);

  const loadTeams = async () => { const { data } = await api.get("/teams"); setTeams(data); };
  const loadFiles = async () => { const { data } = await api.get("/documents", { params: filter ? { team_id: filter } : {} }); setDocs(data); };
  const loadCollab = async () => { const { data } = await api.get("/collab-docs", { params: filter ? { team_id: filter } : {} }); setCollab(data); };

  useEffect(() => { loadTeams(); }, []);
  useEffect(() => { tab === "files" ? loadFiles() : loadCollab(); }, [filter, tab]); // eslint-disable-line

  const fetchTeamMembers = async (tid) => {
    if (!tid) return [];
    try { const { data } = await api.get(`/teams/${tid}/members`); return data; } catch { return []; }
  };

  // upload team change -> load members
  useEffect(() => { fetchTeamMembers(teamId).then((m) => { setUploadMembers(m); setUploadSelected([]); }); }, [teamId]);
  useEffect(() => { fetchTeamMembers(form.team_id).then((m) => { setCollabMembers(m); setCollabSelected([]); }); }, [form.team_id]);

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!teamId) { toast.error("Pick a team first so you can choose who can access this file."); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    fd.append("team_id", teamId);
    fd.append("folder", folder.trim());
    fd.append("allowed_member_ids", JSON.stringify(uploadSelected));
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(uploadSelected.length ? `Uploaded — shared with ${uploadSelected.length} member(s)` : "Uploaded (no one has access yet — use Manage access)");
      setTitle(""); setUploadSelected([]); loadFiles();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const del = (id) => { const d = docs.find((x) => x.id === id); setDocs((l) => l.filter((x) => x.id !== id)); undoableDelete({ label: d?.title || "Document", entityType: "document", perform: () => api.delete(`/documents/${id}`), onUndo: loadFiles }); };

  const addCollab = async (e) => {
    e.preventDefault();
    if (!form.team_id) { toast.error("Pick a team so you can choose who can access this document."); return; }
    try {
      await api.post("/collab-docs", { ...form, folder: (form.folder || "").trim(), allowed_member_ids: collabSelected });
      toast.success("Document added");
      setOpen(false); setForm({ title: "", url: "", team_id: "", folder: "" }); setCollabSelected([]); loadCollab();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delCollab = (id) => {
    const c = collab.find((x) => x.id === id);
    if (active?.id === id) setActive(null);
    setCollab((l) => l.filter((x) => x.id !== id));
    undoableDelete({ label: c?.title || "Shared document", entityType: "collab-doc", perform: () => api.delete(`/collab-docs/${id}`), onUndo: loadCollab });
  };

  const openAccess = async (type, doc) => {
    setAccessFor({ type, doc });
    setAccessSelected(doc.allowed_member_ids || []);
    setAccessFolder(doc.folder || "");
    const m = await fetchTeamMembers(doc.team_id);
    setAccessMembers(m);
  };
  const saveAccess = async () => {
    setSavingAccess(true);
    const url = accessFor.type === "file" ? `/documents/${accessFor.doc.id}/access` : `/collab-docs/${accessFor.doc.id}/access`;
    try {
      await api.put(url, { allowed_member_ids: accessSelected, folder: accessFolder.trim() });
      toast.success("Access updated");
      setAccessFor(null);
      accessFor.type === "file" ? loadFiles() : loadCollab();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingAccess(false); }
  };

  const openBulk = () => {
    setBulkOpen(true); setBulkTeam("");
    setBulkFiles([]); setBulkCollab([]); setBulkMembers([]);
    setBulkSelFiles([]); setBulkSelCollab([]); setBulkSelMembers([]);
  };
  useEffect(() => {
    if (!bulkTeam) { setBulkFiles([]); setBulkCollab([]); setBulkMembers([]); return; }
    (async () => {
      try {
        const [f, c, m] = await Promise.all([
          api.get("/documents", { params: { team_id: bulkTeam } }),
          api.get("/collab-docs", { params: { team_id: bulkTeam } }),
          fetchTeamMembers(bulkTeam),
        ]);
        setBulkFiles(f.data); setBulkCollab(c.data); setBulkMembers(m);
      } catch { /* noop */ }
      setBulkSelFiles([]); setBulkSelCollab([]); setBulkSelMembers([]);
    })();
  }, [bulkTeam]); // eslint-disable-line

  const bulkGroups = () => {
    const g = {};
    bulkFiles.forEach((d) => { const f = d.folder || "Uncategorised"; (g[f] = g[f] || []).push(d); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  };
  const toggleBulkFolder = (items) => {
    const ids = items.map((d) => d.id);
    const allOn = ids.every((id) => bulkSelFiles.includes(id));
    setBulkSelFiles(allOn ? bulkSelFiles.filter((id) => !ids.includes(id)) : [...new Set([...bulkSelFiles, ...ids])]);
  };
  const doBulkShare = async () => {
    if (!bulkSelMembers.length) { toast.error("Pick at least one member to share with."); return; }
    if (!bulkSelFiles.length && !bulkSelCollab.length) { toast.error("Pick at least one document or folder."); return; }
    setSharing(true);
    try {
      const { data } = await api.post("/documents/bulk-share", {
        team_id: bulkTeam, file_ids: bulkSelFiles, collab_ids: bulkSelCollab, member_ids: bulkSelMembers,
      });
      toast.success(`Shared ${data.documents_updated} document${data.documents_updated === 1 ? "" : "s"} with ${bulkSelMembers.length} member${bulkSelMembers.length === 1 ? "" : "s"}`);
      setBulkOpen(false); loadFiles(); loadCollab();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  const matchesSearch = (d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [d.title, d.folder, d.team_name, d.original_filename, d.ext].some((v) => (v || "").toLowerCase().includes(q));
  };
  const visibleDocs = docs.filter(matchesSearch);
  const visibleCollab = collab.filter(matchesSearch);
  const folderSuggestions = [...new Set([...docs, ...collab].map((d) => d.folder).filter(Boolean))];
  const pinnedFolders = filter
    ? ((teams.find((t) => t.id === filter)?.pinned_folders) || [])
    : [...new Set(teams.flatMap((t) => t.pinned_folders || []))];
  const groupByFolder = (items) => {
    const groups = {};
    items.forEach((d) => { const f = d.folder || "Uncategorised"; (groups[f] = groups[f] || []).push(d); });
    return Object.entries(groups).sort((a, b) => {
      const pa = pinnedFolders.indexOf(a[0]), pb = pinnedFolders.indexOf(b[0]);
      if (pa >= 0 && pb >= 0) return pa - pb;
      if (pa >= 0) return -1;
      if (pb >= 0) return 1;
      if (a[0] === "Uncategorised") return 1;
      if (b[0] === "Uncategorised") return -1;
      return a[0].localeCompare(b[0]);
    });
  };
  const togglePin = async (folderName) => {
    if (!filter) return;
    const team = teams.find((t) => t.id === filter);
    const cur = team?.pinned_folders || [];
    const on = cur.includes(folderName);
    const next = on ? cur.filter((f) => f !== folderName) : [...cur, folderName];
    try { await api.put(`/teams/${filter}/pinned-folders`, { folders: next }); await loadTeams(); toast.success(on ? "Folder unpinned" : "Folder pinned to top"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (active) {
    const t = COLLAB_TYPE[active.doc_type] || COLLAB_TYPE.other;
    return (
      <div className="max-w-7xl mx-auto space-y-4 h-[calc(100vh-8rem)] flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="collab-back-button" onClick={() => setActive(null)} className="p-2 rounded-lg border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)" }}><ArrowLeft className="h-4 w-4" /></button>
            <div className="min-w-0">
              <h1 className="font-head text-2xl font-extrabold tracking-tight truncate">{active.title}</h1>
              <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>{t.label}{active.team_name ? ` · ${active.team_name}` : ""} · added by {active.added_by}</span>
            </div>
          </div>
          <a data-testid="collab-open-new-tab" href={active.url} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white font-medium hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
            <ExternalLink className="h-4 w-4" /> Open in Google
          </a>
        </div>
        <div className={`${CARD} flex-1 overflow-hidden p-0`}>
          <iframe title={active.title} src={active.url} data-testid="collab-iframe" className="w-full h-full border-0" allow="clipboard-write" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Team Documents</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Share files and Google Docs with a team — access is granted per member, so only the people you choose can open them.</p>
      </div>

      <div className="flex gap-1 p-1 rounded-full w-fit" style={{ background: "var(--sj-peach)" }}>
        <button data-testid="tab-files" onClick={() => setTab("files")}
          className="flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all"
          style={tab === "files" ? { background: "#fff", color: "var(--sj-primary)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" } : { color: "var(--sj-text-2)" }}>
          <FolderOpen className="h-4 w-4" /> Files
        </button>
        <button data-testid="tab-collab" onClick={() => setTab("collab")}
          className="flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all"
          style={tab === "collab" ? { background: "#fff", color: "var(--sj-primary)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" } : { color: "var(--sj-text-2)" }}>
          <Cloud className="h-4 w-4" /> Collaborative
        </button>
      </div>

      {tab === "files" && (
        <div className={`${CARD} p-6 space-y-4`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Title (optional)</label>
              <input data-testid="doc-title-input" placeholder="Document title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Team</label>
              <select data-testid="doc-team-select" value={teamId} onChange={(e) => setTeamId(e.target.value)} className={inputCls}>
                <option value="">Select a team…</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Folder (optional)</label>
              <input data-testid="doc-folder-input" list="folder-suggestions" placeholder="e.g. Rotas, Policies" value={folder} onChange={(e) => setFolder(e.target.value)} className={inputCls} />
              <span className="text-[11px] mt-1 block" style={{ color: "var(--sj-text-3)" }}>Start typing to reuse an existing folder.</span>
              <datalist id="folder-suggestions">{folderSuggestions.map((f) => <option key={f} value={f} />)}</datalist>
            </div>
          </div>
          {teamId && (
            <div className="rounded-xl p-4" style={{ background: "var(--sj-bg)" }} data-testid="doc-members-picker">
              <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>
                <Users className="h-3.5 w-3.5" /> Who can access this file?
              </div>
              <MemberPicker members={uploadMembers} selected={uploadSelected}
                onToggle={(id) => toggle(uploadSelected, setUploadSelected, id)}
                onSelectAll={(on) => setUploadSelected(on ? uploadMembers.map((m) => m.id) : [])}
                testidPrefix="doc-upload" />
            </div>
          )}
          <div>
            <input ref={fileRef} data-testid="doc-file-input" type="file" onChange={upload} className="hidden" />
            <button data-testid="upload-doc-button" disabled={uploading || !teamId} onClick={() => fileRef.current?.click()}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
              <Upload className="h-5 w-5" /> {uploading ? "Uploading..." : "Upload File"}
            </button>
            {!teamId && <span className="ml-3 text-xs" style={{ color: "var(--sj-text-3)" }}>Select a team first</span>}
          </div>
        </div>
      )}

      {tab === "collab" && (
        <div className="flex justify-end">
          <button data-testid="add-collab-doc-button" onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-medium shadow-sm hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>
            <Plus className="h-5 w-5" /> Add Google Doc / Sheet
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-primary)" }} />
          <input data-testid="documents-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents by name, folder or team…"
            className="w-full bg-white rounded-full border-2 border-[#0B5E4E] shadow-sm pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/30" />
        </div>
        <button data-testid="bulk-share-button" onClick={openBulk}
          className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border hover:bg-[#EDF2F4] transition-colors shrink-0" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
          <Share2 className="h-4 w-4" /> Bulk share
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className="rounded-full px-4 py-2 text-sm font-medium border transition-all" style={filter === "" ? { background: "var(--sj-secondary)", color: "#fff", borderColor: "var(--sj-secondary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>All</button>
        {teams.map((t) => (
          <button key={t.id} onClick={() => setFilter(t.id)} className="rounded-full px-4 py-2 text-sm font-medium border transition-all" style={filter === t.id ? { background: "var(--sj-secondary)", color: "#fff", borderColor: "var(--sj-secondary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>{t.name}</button>
        ))}
      </div>

      {tab === "files" && (
        <div className="space-y-6" data-testid="documents-grid">
          {visibleDocs.length === 0 && <div className={`${CARD} p-12 text-center`} style={{ color: "var(--sj-text-3)" }}>{search.trim() ? `No files match "${search}".` : "No files yet. Pick a team and upload your first file above."}</div>}
          {groupByFolder(visibleDocs).map(([folderName, items]) => (
            <div key={folderName} data-testid={`folder-group-${folderName}`}>
              <div className="flex items-center gap-2 mb-3 group">
                <FolderOpen className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
                <h3 className="font-head text-base font-bold" style={{ color: "var(--sj-text)" }}>{folderName}</h3>
                <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>· {items.length}</span>
                {filter && folderName !== "Uncategorised" && (
                  <button data-testid={`pin-folder-${folderName}`} onClick={() => togglePin(folderName)}
                    title={pinnedFolders.includes(folderName) ? "Unpin folder" : "Pin folder to top"}
                    className={`ml-1 p-1 rounded-md hover:bg-[#EDF2F4] transition-all ${pinnedFolders.includes(folderName) ? "" : "opacity-0 group-hover:opacity-100"}`}>
                    {pinnedFolders.includes(folderName)
                      ? <Pin className="h-3.5 w-3.5" style={{ color: "var(--sj-primary)", fill: "var(--sj-primary)" }} />
                      : <PinOff className="h-3.5 w-3.5" style={{ color: "var(--sj-text-3)" }} />}
                  </button>
                )}
                {pinnedFolders.includes(folderName) && !filter && <Pin className="h-3 w-3" style={{ color: "var(--sj-primary)", fill: "var(--sj-primary)" }} />}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((d) => {
                  const Icon = iconFor(d.ext);
                  const n = (d.allowed_member_ids || []).length;
                  return (
                    <div key={d.id} data-testid={`doc-${d.id}`} className={`${CARD} p-5 flex items-start gap-4 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(45,45,45,0.08)]`}>
                      <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--sj-peach)" }}><Icon className="h-6 w-6" style={{ color: "var(--sj-primary)" }} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{d.title}</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{d.ext?.toUpperCase()} · {humanSize(d.size)}{d.team_name ? ` · ${d.team_name}` : ""}</div>
                        <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: n ? "var(--sj-primary)" : "#B45309" }} data-testid={`doc-access-${d.id}`}>
                          <Lock className="h-3 w-3" /> {n ? `Shared with ${n} member${n > 1 ? "s" : ""}` : "No members can access yet"}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button data-testid={`download-doc-${d.id}`} onClick={() => download(d)} className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}><Download className="h-3.5 w-3.5" />Download</button>
                          <button data-testid={`manage-access-${d.id}`} onClick={() => openAccess("file", d)} className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}><Users className="h-3.5 w-3.5" />Manage</button>
                          <button data-testid={`delete-doc-${d.id}`} onClick={() => del(d.id)} className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "collab" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="collab-grid">
          {visibleCollab.length === 0 && (
            <div className={`${CARD} p-12 text-center col-span-full flex flex-col items-center gap-2`} style={{ color: "var(--sj-text-3)" }}>
              <Cloud className="h-10 w-10" />
              <div>{search.trim() ? `No documents match "${search}".` : "No collaborative docs yet."}</div>
            </div>
          )}
          {visibleCollab.map((d) => {
            const t = COLLAB_TYPE[d.doc_type] || COLLAB_TYPE.other;
            const Icon = t.icon;
            const n = (d.allowed_member_ids || []).length;
            return (
              <div key={d.id} data-testid={`collab-card-${d.id}`} className={`${CARD} p-5 flex flex-col group`}>
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: t.bg }}>
                    <Icon className="h-6 w-6" style={{ color: t.color }} />
                  </div>
                  <button data-testid={`collab-delete-${d.id}`} onClick={() => delCollab(d.id)} className="p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="mt-3 font-semibold" style={{ color: "var(--sj-text)" }}>{d.title}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{t.label}{d.team_name ? ` · ${d.team_name}` : ""} · {d.added_by}</div>
                <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: n ? "var(--sj-primary)" : "#B45309" }} data-testid={`collab-access-${d.id}`}>
                  <Lock className="h-3 w-3" /> {n ? `Shared with ${n} member${n > 1 ? "s" : ""}` : "No members can access yet"}
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`collab-open-${d.id}`} onClick={() => setActive(d)}
                    className="flex-1 rounded-full py-2 text-sm font-semibold border transition-all hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
                    Open &amp; edit
                  </button>
                  <button data-testid={`collab-access-btn-${d.id}`} onClick={() => openAccess("collab", d)}
                    className="rounded-full px-3 py-2 text-sm font-semibold border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}><Users className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add collab dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Add a shared Google document</DialogTitle></DialogHeader>
          <form onSubmit={addCollab} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Title</label>
              <input data-testid="collab-title-input" required placeholder="e.g. 2026 Budget Plan" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Google Docs / Sheets link</label>
              <input data-testid="collab-url-input" required placeholder="https://docs.google.com/..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Team</label>
              <select data-testid="collab-team-select" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} className={inputCls}>
                <option value="">Select a team…</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Folder (optional)</label>
              <input data-testid="collab-folder-input" list="folder-suggestions" placeholder="e.g. Rotas, Policies" value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} className={inputCls} />
            </div>
            {form.team_id && (
              <div className="rounded-xl p-4" style={{ background: "var(--sj-bg)" }}>
                <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>
                  <Users className="h-3.5 w-3.5" /> Who can access this document?
                </div>
                <MemberPicker members={collabMembers} selected={collabSelected}
                  onToggle={(id) => toggle(collabSelected, setCollabSelected, id)}
                  onSelectAll={(on) => setCollabSelected(on ? collabMembers.map((m) => m.id) : [])}
                  testidPrefix="collab" />
              </div>
            )}
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="save-collab-doc-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>Add Document</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage access dialog */}
      <Dialog open={!!accessFor} onOpenChange={(o) => !o && setAccessFor(null)}>
        <DialogContent data-testid="access-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Manage access</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Choose which members of <strong>{accessFor?.doc?.team_name || "this team"}</strong> can open <strong>{accessFor?.doc?.title}</strong>.</p>
          <div className="mt-1">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Folder</label>
            <input data-testid="access-folder-input" list="folder-suggestions" placeholder="e.g. Rotas, Policies" value={accessFolder} onChange={(e) => setAccessFolder(e.target.value)} className={inputCls} />
          </div>
          <div className="mt-3">
            <div className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Members with access</div>
            <MemberPicker members={accessMembers} selected={accessSelected}
              onToggle={(id) => toggle(accessSelected, setAccessSelected, id)}
              onSelectAll={(on) => setAccessSelected(on ? accessMembers.map((m) => m.id) : [])}
              testidPrefix="access" />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setAccessFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="save-access-btn" disabled={savingAccess} onClick={saveAccess} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{savingAccess ? "Saving..." : "Save access"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk share dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-share-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Bulk share documents</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Pick a team, choose whole folders or individual documents, then grant access to one or more members — all in one go.</p>
          <div className="mt-2">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Team</label>
            <select data-testid="bulk-team-select" value={bulkTeam} onChange={(e) => setBulkTeam(e.target.value)} className={inputCls}>
              <option value="">Select a team…</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {bulkTeam && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* documents column */}
              <div className="rounded-xl p-3 max-h-72 overflow-y-auto" style={{ background: "var(--sj-bg)" }} data-testid="bulk-doc-list">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Documents & folders</div>
                {bulkFiles.length === 0 && bulkCollab.length === 0 && <p className="text-sm py-3" style={{ color: "var(--sj-text-3)" }}>This team has no documents yet.</p>}
                {bulkGroups().map(([folderName, items]) => {
                  const ids = items.map((d) => d.id);
                  const allOn = ids.every((id) => bulkSelFiles.includes(id));
                  return (
                    <div key={folderName} className="mb-3">
                      <button type="button" data-testid={`bulk-folder-${folderName}`} onClick={() => toggleBulkFolder(items)}
                        className="w-full flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "var(--sj-primary)" }}>
                        <FolderInput className="h-4 w-4" /> {folderName}
                        <span className="text-[11px] font-normal" style={{ color: "var(--sj-text-3)" }}>({items.length})</span>
                        <span className="ml-auto text-[11px]">{allOn ? "Clear folder" : "Select folder"}</span>
                      </button>
                      <div className="space-y-1 pl-1">
                        {items.map((d) => {
                          const on = bulkSelFiles.includes(d.id);
                          return (
                            <button type="button" key={d.id} data-testid={`bulk-file-${d.id}`} onClick={() => toggle(bulkSelFiles, setBulkSelFiles, d.id)}
                              className="w-full flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm"
                              style={on ? { background: "#0B5E4E1A", color: "var(--sj-primary)" } : { color: "var(--sj-text)", border: "1px solid var(--sj-border)" }}>
                              <span className="truncate flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 shrink-0" />{d.title}</span>
                              <span className="h-4 w-4 rounded flex items-center justify-center shrink-0" style={on ? { background: "var(--sj-primary)" } : { border: "1px solid var(--sj-border)" }}>{on && <Check className="h-3 w-3 text-white" />}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {bulkCollab.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider mb-1 mt-2" style={{ color: "var(--sj-text-3)" }}>Google docs</div>
                    <div className="space-y-1 pl-1">
                      {bulkCollab.map((d) => {
                        const on = bulkSelCollab.includes(d.id);
                        return (
                          <button type="button" key={d.id} data-testid={`bulk-collab-${d.id}`} onClick={() => toggle(bulkSelCollab, setBulkSelCollab, d.id)}
                            className="w-full flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm"
                            style={on ? { background: "#0B5E4E1A", color: "var(--sj-primary)" } : { color: "var(--sj-text)", border: "1px solid var(--sj-border)" }}>
                            <span className="truncate flex items-center gap-1.5"><Cloud className="h-3.5 w-3.5 shrink-0" />{d.title}</span>
                            <span className="h-4 w-4 rounded flex items-center justify-center shrink-0" style={on ? { background: "var(--sj-primary)" } : { border: "1px solid var(--sj-border)" }}>{on && <Check className="h-3 w-3 text-white" />}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* members column */}
              <div className="rounded-xl p-3" style={{ background: "var(--sj-bg)" }} data-testid="bulk-member-list">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Share with</div>
                <MemberPicker members={bulkMembers} selected={bulkSelMembers}
                  onToggle={(id) => toggle(bulkSelMembers, setBulkSelMembers, id)}
                  onSelectAll={(on) => setBulkSelMembers(on ? bulkMembers.map((m) => m.id) : [])}
                  testidPrefix="bulk" />
              </div>
            </div>
          )}

          <DialogFooter className="mt-2">
            <div className="mr-auto text-xs self-center" style={{ color: "var(--sj-text-3)" }}>
              {(bulkSelFiles.length + bulkSelCollab.length)} document(s) · {bulkSelMembers.length} member(s)
            </div>
            <button type="button" onClick={() => setBulkOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="do-bulk-share-btn" disabled={sharing || !bulkTeam || !bulkSelMembers.length || (!bulkSelFiles.length && !bulkSelCollab.length)} onClick={doBulkShare}
              className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{sharing ? "Sharing..." : "Share"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
